import PopupBasic from 'src/game/components/popups/PopupBasic';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { createEmitter } from 'src/lib/Emitter';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { ImageScaleView, ScrollView, animate } from '@play-co/timestep-core/ui';
import { animDuration } from 'src/lib/utils';
import { TextMessageID, textMessageIDs } from 'src/replicant/team/team.ruleset';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import uiConfig from 'src/lib/ui/config';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: TextMessageID | undefined) => void;

const ITEM_HEIGHT = 164;
const PANEL_HEIGHT = ITEM_HEIGHT * 4 + 30;

export default class PopupTeamChatMessages extends PopupBasic {
  private panel: ImageScaleView;
  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      ...opts,
      close: () => opts.close(undefined),
      zIndex: 10001,
      width: 556,
      height: 455,
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.POPUP_TEXT_MESSAGES,
      },
      skipTitle: true,
      closeableWithBg: true,
    });

    this.box.removeFromSuperview();

    this.panel = new ImageScaleView({
      superview: this.root,
      width: uiConfig.width,
      height: PANEL_HEIGHT,
      image: 'assets/ui/match2/team/chat/panel_emoteBar.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 2, right: 2 },
        vertical: { top: 8, bottom: 2 },
      },
    });

    const scroll = new ScrollView({
      superview: this.panel,
      width: uiConfig.width,
      height: PANEL_HEIGHT,
      scrollY: true,
      scrollX: false,
      useContentBounds: true,
    });

    const container = new FlexBoxView({
      superview: scroll,
      x: 0,
      y: 0,
      width: uiConfig.width,
      height: ITEM_HEIGHT * 8,
      flex: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-end',
      },
    });

    for (let messageId of textMessageIDs) {
      const button = new ButtonScaleView({
        superview: container,
        width: 630,
        height: ITEM_HEIGHT,
        image: 'assets/ui/match2/team/chat/panel_chatbubble.png',
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: { left: 40, right: 100 },
          vertical: { top: 100, bottom: 40 },
        },
        onClick: async () => {
          sounds.sfx.play('ok');
          opts.close(messageId);
        },
        tracking: {
          feature: Feature.MATCH2.TEAM.POPUP_TEXT_MESSAGES,
          $subFeature: messageId,
        },
      });

      new LangBitmapFontTextView({
        superview: button,
        localeText: () => i18n(`match2.team.chat.messages.${messageId}`),
        align: 'center',
        verticalAlign: 'center',
        font: 'Body',
        width: 630,
        height: 164,
        size: 26,
        color: '#373658',
      });
    }

    // anchor elements
    createEmitter(this.root, ({ ui }) => ui.screenSize).addListener(
      (screen) => {
        this.panel.updateOpts({
          opacity: 1,
          y: screen.bottom - PANEL_HEIGHT,
        });

        this.bg.updateOpts({
          width: screen.width,
          height: screen.height,
        });
      },
    );
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/match2/team/chat/panel_emoteBar.png',
      'assets/ui/match2/team/chat/panel_chatbubble.png',
    ]);
  }

  public async fadeIn() {
    this.overlay.show();
    this.attachRoot();

    this.bg.updateOpts({ opacity: 0 });

    this.panel.updateOpts({
      offsetY: PANEL_HEIGHT,
    });

    animate(this.panel)
      .clear()
      .then({ offsetY: 0 }, animDuration, animate.easeOut)
      .then(() => this.overlay.hide());
  }

  public fadeOut() {
    this.overlay.show();

    animate(this.bg)
      .clear()
      .wait(animDuration)
      .then({ opacity: 0 }, animDuration, animate.easeOut)
      .then(() => {
        this.detachRoot();
        this.overlay.hide();
      });

    animate(this.panel)
      .clear()
      .then({ offsetY: PANEL_HEIGHT }, animDuration, animate.easeOut);
  }

  public init(opts: {}) {
    super.init(opts);
  }
}
